import Layout from '../../components/layout/Layout'
import ExerciseCard from '@/components/ui/exercise-card'
import chibatore2Data from '@/data/thoughts/chibatore.json'
import { ASSETS } from '@/constants/assets'
import { useState, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'

const CollapsibleSection = ({
  title,
  children,
  isOpen,
  onToggle
}: {
  title: string
  children: React.ReactNode
  isOpen: boolean
  onToggle: () => void
}) => {
  return (
    <div className="border-b border-foreground/10">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-[1.953rem] font-semibold text-foreground font-sans tracking-[-0.025em] leading-[1.2] [text-wrap:balance]"
      >
        <span>{title}</span>
        <ChevronDown
          className={cn(
            "w-6 h-6 transition-transform duration-200",
            isOpen ? "transform rotate-180" : ""
          )}
        />
      </button>
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="py-4">{children}</div>
        </div>
      </div>
    </div>
  )
}

const ThoughtChibatore2 = () => {
  const [openSections, setOpenSections] = useState({
    resources: false,
    skills: false,
    concepts: false
  })

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // State for hover/tap effect
  const [isHovered, setIsHovered] = useState(false)
  const [isClicked, setIsClicked] = useState(false)
  const [ignoreHover, setIgnoreHover] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const dynamicBridgeExercise = chibatore2Data.exercises.find(
    ex => ex.title === "Dynamic Bridge"
  )

  const closeModal = () => {
    setShowModal(false)
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (showModal) window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [showModal])

  return (
    <Layout>
      <div className="w-full max-w-4xl px-4 md:px-8 lg:px-12 py-12 space-y-6">
        <h1 className="text-[2.488rem] font-semibold text-foreground font-sans tracking-[-0.025em] leading-[1.2] [text-wrap:balance]">
          Chibatore
        </h1>
        <p className="text-foreground/60 text-[1.375rem] leading-[1.3] [text-wrap:balance]">
          High-quality body movement to improve your health.
        </p>

        {/* Image container */}
        <div 
          className="relative flex justify-center w-full max-w-xl mx-auto cursor-pointer"
          onMouseEnter={() => !ignoreHover && setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false)
            setIgnoreHover(false)
          }}
          onClick={() => {
            if (isHovered && isClicked) {
              setIgnoreHover(true)
            }
            setIsClicked(prev => !prev)
          }}
        >
          {/* Base image */}
          <img 
            src={ASSETS.images.chibatoreDiagram} 
            alt="Chibatore Diagram" 
            className="w-full h-auto" 
          />
          {/* Highlight image */}
          <img 
            src={ASSETS.images.chibatoreDiagramHighlight}
            alt="Chibatore Diagram Highlight"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              (isHovered && !ignoreHover) || isClicked ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        <CollapsibleSection
          title="Resources"
          isOpen={openSections.resources}
          onToggle={() => toggleSection('resources')}
        >
          <p>
            Content for Resources goes here.
          </p>
        </CollapsibleSection>

        <CollapsibleSection
          title="Skills"
          isOpen={openSections.skills}
          onToggle={() => toggleSection('skills')}
        >
          <p>
            Content for Skills goes here.
          </p>
        </CollapsibleSection>

        <CollapsibleSection
          title="Concepts"
          isOpen={openSections.concepts}
          onToggle={() => toggleSection('concepts')}
        >
          <p>
            Content for Concepts goes here.
          </p>
        </CollapsibleSection>
      </div>

      {showModal && dynamicBridgeExercise && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeModal}
        >
          <div 
            className="w-full max-w-2xl relative flex items-center"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 text-white hover:text-white/80 transition-colors rounded-full bg-black/50 hover:bg-black/70 z-50"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
            
            <ExerciseCard
              {...dynamicBridgeExercise}
              className="w-full h-auto max-h-[calc(100vh-4rem)]"
            />
          </div>
        </div>
      )}
    </Layout>
  )
}

export default ThoughtChibatore2 